//* Libraries imports
import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/app/store";

//* Component imports
import ApplyTagsModal from "@/components/ui/ApplyTagsModal";

//* Hooks imports
import { useCreateTemplateHistory } from "@/hooks/template-history/mutations/useCreateTemplateHistory";
import { useFetchTemplateHistory } from "@/hooks/template-history/queries/useFetchTemplateHistory";
import { useDeleteTemplateHistory } from "@/hooks/template-history/mutations/useDeleteTemplateHistory";
import { fetchTemplateById, TemplateActions } from "./TemplateSlice";

export default function ApplyTags(props: {
  isOpen: boolean;
  onClose: () => void;
  template: any;
  clientId?: string;
}) {
  const dispatch = useDispatch<AppDispatch>();
  const templateState = useSelector((state: RootState) => state.template);
  const currentItem = templateState.currentItem;

  const createHistory = useCreateTemplateHistory();
  const fetchHistory = useFetchTemplateHistory();
  const deleteHistoryItem = useDeleteTemplateHistory();

  const loadData = useCallback(async () => {
    if (props.template?._id) {
      await dispatch(fetchTemplateById(props.template._id));
    }
  }, [dispatch, props.template]);

  useEffect(() => {
    if (props.isOpen) {
      loadData();
    }
    return () => {
      dispatch(TemplateActions.clearCurrentItem());
    };
  }, [props.isOpen, loadData, dispatch]);

  const handleGenerate = async (
    replacements: any,
    mappingRules: any,
    clientId?: string,
  ) => {
    try {
      const result = await createHistory.create({
        templateId: props.template._id,
        clientId: clientId || undefined,
        type: "automatic",
        replacements: replacements,
        mappingRules: mappingRules,
      });

      const url = result?.documentUrl;
      if (url) {
        window.open(url, "_blank");
        if (clientId) {
          fetchHistory.fetchAll({
            templateId: props.template._id,
            clientId: clientId,
          });
        }
      }
    } catch (e) {
      console.error("Error generating test document:", e);
      alert("Erro ao gerar documento de teste. Tente novamente.");
    }
  };

  const handleUpload = async (file: File, clientId: string) => {
    await createHistory.create({
      templateId: props.template._id,
      clientId: clientId,
      type: "upload",
      file: file,
    });
  };

  const handleHistoryFetch = (filters: {
    clientId?: string;
    type?: "automatic" | "upload";
    startDate?: string;
    endDate?: string;
  }) => {
    if (props.template?._id) {
      fetchHistory.fetchAll({
        templateId: props.template._id,
        ...filters,
      });
    }
  };

  return (
    <ApplyTagsModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      entity={currentItem || props.template}
      acceptedFileTypes=".pptx"
      onGenerate={handleGenerate}
      onUpload={handleUpload}
      history={{
        items: fetchHistory.items,
        loading: fetchHistory.loading,
        onDelete: async (id) => {
          await deleteHistoryItem.remove(id);
        },
        onFetch: handleHistoryFetch,
      }}
      isGenerating={createHistory.loading}
      title={`Aplicar template "${props.template?.name}" em cliente`}
      clientId={props.clientId}
    />
  );
}
